/**
 * @module reportsManager
 * @description This manager provides methods to report errors to an error management system.
 */
 import { Validator } from "jsonschema";
 import {
   parameterSchema,
   deviceSchema,
   dumpSchema,
   networkSchema,
   playbackInfoSchema,
   reportBodySchema,
   eventsSchema
 } from "../../schema/index";
 import { services } from "../../constants";
 import errors from "../errors";
 import { geolocation, playbackToken, sessionId } from "../store";
 
 const validator = new Validator();
 
 validator.addSchema(parameterSchema);
 validator.addSchema(deviceSchema);
 validator.addSchema(dumpSchema);
 validator.addSchema(networkSchema);
 validator.addSchema(playbackInfoSchema);
 validator.addSchema(eventsSchema);
 
 /**
  * Gets an object with static values coming from SDK context to create a JSON blueprint for Splunk reporting.
  *
  * @param {Object} payloadObject - Payload object coming from request method call.
  */
 async function getBasePayload(payloadObject) {
   const geolocationData = geolocation.get();
   const baseObject = {
     device: {
       modelName: await this.systemManager.device.getRealModel(),
       osVersion: "",
       fwVersion: await this.systemManager.device.getFirmwareVersion(),
       swVersion: await this.systemManager.device.getServerVersion(),
       userAgent: navigator.userAgent || (await this.systemManager.device.getPlatform()),
       modelYear: await this.systemManager.device.getYear()
     },
     appVersion: this.publicVersion,
     buildVersion: this.appVersion,
     appLanguage: this.lang,
     network: {
       connectionType: await this.systemManager.network.getConnectionType(),
       country: (geolocationData && geolocationData.country) || ""
     }
   };
   const currentAudioTrack = await this.playerManager.getCurrentAudioTrack();
   const currentSubtitlesTrack = await this.playerManager.getCurrentSubtitleTrack();
 
   return {
     ...baseObject,
     ...(payloadObject.parameter.playbackInfo && {
       playbackInfo: {
         ...payloadObject.parameter.playbackInfo,
         quality: !!payloadObject.parameter.playbackInfo.quality ? "4K" : "HD",
         ...(!!playbackToken.get() && { vualtoToken: playbackToken.get() }),
         currentBitrate: this.playerManager.getCurrentBitrate(),
         availableBandwidth: await this.playerManager.getCurrentBandwidth(),
         currentTime: await this.playerManager.getCurrentTime(),
         audioLang: (currentAudioTrack && currentAudioTrack.title) || "",
         subsLang: (currentSubtitlesTrack && currentSubtitlesTrack.title) || "",
         sessionId: sessionId.get()
       }
     })
   };
 }
 
 /**
  * Validates payload object to be sent in report request.
  *
  * @param {Object} payloadObject - Payload object to be sent in report request
  */
 function validateReportPayload(payloadObject) {
   const validation = validator.validate(payloadObject, reportBodySchema);
   if (validation.errors.length) {
     throw Error(`The JSON for report is not valid
      ${validation.errors.join("\n       ")}`);
   }
 }
 
 /**
  * Creates a payload object mixing static values in the SDK and what comes in request method.
  *
  * @param {Object} payloadObject - Payload object.
  */
 async function getPayloadObject(payloadObject) {
   return {
     ...payloadObject,
     parameter: { ...payloadObject.parameter, ...(await getBasePayload.call(this, payloadObject)) }
   };
 }
 
 /**
  * Gets device's model/year.
  */
 async function getModel() {
   const platform = await this.systemManager.device.getPlatform();
   const year = await this.systemManager.device.getYear();
   return platform + year;
 }
 
 /**
  * Validates the object which will be sent in /events request.
  * @param {*} eventData - Event request body object. Needs to be 'null' by default to
  * avoid 'undefined' parameter passes validation.
  */
 function isEventDataValid(eventData = null) {
   const validation = validator.validate(eventData, eventsSchema);
   validation.errors.length && console.warn(`${errors.events.WRONG_EVENT_PARAMS}`);
   return !validation.errors.length;
 }
 
 /**
  * Returns data object to send in the request
  * @param {Object} data
  */
 async function getEventDataFormatted(data) {
   const { event_type, media_details } = data;
   return {
     event_type,
     event_params: {
       media_details,
       date: Math.floor(+new Date() / 1000),
       origin: await getModel.call(this)
     }
   };
 }
 
 /**
  * Reports Manager mixing methods for the SDK (Splunk).
  */
 export default {
   /**
    * Posts a JSON payload for the reporting service.
    *
    * @param level
    * @param event
    * @param env
    * @param payload
    * @return {Promise<void>}
    */
   async sendReport({ level, event, env }, payload) {
     const payloadObject = await getPayloadObject.call(this, payload);
     // TODO: Flatten the payload schema in order to simplify validation. Maybe create specialized level methods.
     validateReportPayload(payloadObject);
 
     try {
       const origin = await getModel.call(this);
       await this.http.post(
         this.buildUrlReport(origin, { level, event, env }),
         payloadObject,
         await this.getHeaders.call(this)
       );
     } catch (e) {
       console.log("Report could not be sent.");
     }
   },
   /**
    * Posts a JSON user event data.
    * @param {Object} eventData
    */
   async sendRecEngineEvent(eventData) {
     if (isEventDataValid(eventData)) {
       const eventDataFormatted = await getEventDataFormatted.call(this, eventData);
       this.http.post(`${this.backendHosts.media}${services.events}`, eventDataFormatted, await this.getHeaders());
     }
   },
   /**
    * Posts a JSON reporst data.
    * @param {Object} eventData
    */
   async submitHelpInquiry(data) {
     const url = `${this.backendHosts.reports}${services.support}`;
     try {
       await this.http.post(url, data, await this.getHeaders());
     } catch (error) {
       this.handleErrorResponse(error);
     }
   },
   /**
    * Returns FAQ categories information.
    * @param {String} language
    * @param {String} tenantId
    */
   async getFAQCategories(language , tenantId) {
     const url = `${this.backendHosts.reports}${services.faqCategories}`;
     try {
       const categories = await this.http.get(url, {
         params: {
           language ,
           tenantId
         }
       });
       return categories.data;
     } catch (error) {
       this.handleErrorResponse(error);
     }
   },
   /**
    * Returns FAQ data.
    * @param {String} language
    * @param {String} subject It's used for the search. It's the search term
    * @param {String} tenantId
    */
   async getFaq(language, subject = null , tenantId) {
     const url = `${this.backendHosts.reports}${services.faq}`;
     const params = {
       language,
       tenantId,
       ...(subject && { subject: encodeURIComponent(subject) })
     };
     try {
       const faqs = await this.http.get(url, { params });
       return faqs.data;
     } catch (error) {
       this.handleErrorResponse(error);
     }
   }
 };
 